/**
 * Login component for Microsoft Entra ID authentication.
 */
import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Avatar,
  Chip,
  Stack,
  Paper
} from '@mui/material';
import {
  Login as LoginIcon,
  Logout as LogoutIcon,
  AccountCircle,
  Microsoft as MicrosoftIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { useAuthConfig } from '../contexts/AuthContext';

const LoginComponent = () => {
  const { 
    isAuthenticated, 
    isLoading, 
    error, 
    user, 
    login, 
    logout,
    clearError 
  } = useAuth();
  
  const { 
    isConfigured, 
    isLoading: configLoading, 
    error: configError 
  } = useAuthConfig();

  // Jeśli konfiguracja się ładuje
  if (configLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Ładowanie konfiguracji uwierzytelniania...
          </Typography>
        </Stack>
      </Box>
    );
  }

  // Jeśli uwierzytelnianie nie jest skonfigurowane
  if (!isConfigured) {
    return (
      <Paper elevation={2} sx={{ p: 3, m: 2, maxWidth: 500, mx: 'auto' }}>
        <Stack spacing={2} alignItems="center">
          <SecurityIcon color="warning" sx={{ fontSize: 48 }} />
          <Typography variant="h6" color="warning.main" textAlign="center">
            Uwierzytelnianie nie skonfigurowane
          </Typography>
          {configError ? (
            <Alert severity="error" sx={{ width: '100%' }}>
              <Typography variant="body2">
                <strong>Błąd konfiguracji:</strong> {configError}
              </Typography>
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ width: '100%' }}>
              <Typography variant="body2">
                Aby skonfigurować uwierzytelnianie Microsoft Entra ID:
              </Typography>
              <Typography variant="body2" component="ol" sx={{ mt: 1, pl: 2 }}>
                <li>Ustaw zmienne środowiskowe KALENDARZ_AZURE_TENANT_ID i KALENDARZ_AZURE_CLIENT_ID</li>
                <li>Uruchom ponownie serwer backend</li>
                <li>Odśwież stronę</li>
              </Typography>
            </Alert>
          )}
        </Stack>
      </Paper>
    );
  }

  // Jeśli użytkownik jest zalogowany
  if (isAuthenticated && user) {
    return (
      <Card elevation={2} sx={{ maxWidth: 400, mx: 'auto', m: 2 }}>
        <CardContent>
          <Stack spacing={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                <AccountCircle />
              </Avatar>
              <Box>
                <Typography variant="h6">
                  {user.name || user.username}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.email}
                </Typography>
              </Box>
            </Box>
            
            <Chip 
              icon={<SecurityIcon />}
              label="Zalogowany przez Microsoft Entra ID"
              color="success"
              variant="outlined"
              size="small"
            />

            <Button
              variant="outlined"
              color="secondary"
              startIcon={isLoading ? <CircularProgress size={16} /> : <LogoutIcon />}
              onClick={logout}
              disabled={isLoading}
              fullWidth
            >
              {isLoading ? 'Wylogowywanie...' : 'Wyloguj'}
            </Button>

            {error && (
              <Alert 
                severity="error" 
                onClose={clearError}
                sx={{ mt: 2 }}
              >
                {error}
              </Alert>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  }

  // Formularz logowania
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: 2,
        zIndex: 9999
      }}
    >
      <Card 
        elevation={24} 
        sx={{ 
          maxWidth: 600, 
          width: '100%',
          borderRadius: 4,
          overflow: 'visible'
        }}
      >
        <CardContent sx={{ p: 6 }}>
          <Stack spacing={4} alignItems="center">
            <Avatar sx={{ 
              bgcolor: 'primary.main', 
              width: 80, 
              height: 80,
              boxShadow: 3
            }}>
              <SecurityIcon sx={{ fontSize: 48 }} />
            </Avatar>
            
            <Typography variant="h3" textAlign="center" fontWeight="bold">
              Kalendarz Pracy
            </Typography>
            
            <Typography variant="h6" color="text.secondary" textAlign="center">
              Zaloguj się używając konta Microsoft, aby uzyskać dostęp do aplikacji
            </Typography>

            <Button
              variant="contained"
              size="large"
              startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
              onClick={login}
              disabled={isLoading}
              fullWidth
              sx={{
                py: 2,
                fontSize: '1.2rem',
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                boxShadow: 4,
                '&:hover': {
                  boxShadow: 8,
                  transform: 'scale(1.02)',
                  transition: 'all 0.2s'
                }
              }}
            >
              {isLoading ? 'Logowanie...' : 'Zaloguj przez Microsoft'}
            </Button>

            {error && (
              <Alert 
                severity="error" 
                onClose={clearError}
                sx={{ width: '100%' }}
              >
                <Typography variant="body1">
                  <strong>Błąd logowania:</strong> {error}
                </Typography>
              </Alert>
            )}
            
            <Box textAlign="center" mt={2}>
              <MicrosoftIcon sx={{ fontSize: 32, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                Bezpieczne uwierzytelnianie przez Microsoft Entra ID
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LoginComponent;